import { onMounted, onBeforeUnmount, ref, watch, nextTick, computed, type Ref } from 'vue';
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap';
import { useAppStore } from '../stores/appStore';

interface UsePopupModalOptions {
  popupID?: string;
  modalRef: Ref<HTMLElement | null>;
}

export const usePopupModal = ({ popupID, modalRef }: UsePopupModalOptions) => {
  const appStore = useAppStore();

  const isModalOpen = ref(false);
  const isDialogElement = computed(() => modalRef.value?.nodeName === 'DIALOG');

  const { activate, deactivate } = useFocusTrap(modalRef, {
    allowOutsideClick: true,
    escapeDeactivates: false,
  });

  const closeModal = () => {
    deactivate();
    nextTick(() => {
      if (isDialogElement.value) {
        (modalRef.value as HTMLDialogElement).close();
      }
      appStore.unblockScrolling();
      isModalOpen.value = false;
    });
  };

  const openModal = () => {
    isModalOpen.value = true;
    appStore.blockScrolling();
    nextTick(() => {
      if (isDialogElement.value) {
        (modalRef.value as HTMLDialogElement).showModal();
      }
      activate();
    });
  };

  const onLinkClick = (event: Event) => {
    const link = (event.target as HTMLElement).closest(`a[href="#${popupID}"]`);
    if (link) {
      event.preventDefault();
      openModal();
    }
  };

  watch(
    () => isModalOpen.value,
    () => {
      if (!isDialogElement.value) {
        nextTick(() => {
          appStore.setBackdrop('PopupModal', { enable: isModalOpen.value, global: true });
        });
      }
    }
  );

  onMounted(() => {
    if (popupID) document.addEventListener('click', onLinkClick);
  });

  onBeforeUnmount(() => {
    if (popupID) document.removeEventListener('click', onLinkClick);
    if (!isDialogElement.value) {
      appStore.setBackdrop('PopupModal', { enable: false });
    }
  });

  return {
    isModalOpen,
    closeModal,
    openModal,
  };
};
